import CloseIcon from '@mui/icons-material/Close';
import { Dialog } from '@mui/material';
import { type ChangeEvent, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type AdvancedImportPayload = {
    file: File;
    actPrompt: string;
    episodePrompt: string;
    scenePrompt: string;
    genericLogline: boolean;
    genericSynopsis: boolean;
    outlineOnly: boolean;
};

interface AdvancedImportDialogProps {
    open: boolean;
    isPending: boolean;
    onClose: () => void;
    onSubmit: (payload: AdvancedImportPayload) => void;
}

const fieldClass =
    'w-full rounded-lg bg-c61 text-c18 dark:text-c1 px-3 py-2 text-sm border-none outline-none resize-y placeholder:text-c45';

export const AdvancedImportDialog = memo(({ open, isPending, onClose, onSubmit }: AdvancedImportDialogProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [actPrompt, setActPrompt] = useState('');
    const [episodePrompt, setEpisodePrompt] = useState('');
    const [scenePrompt, setScenePrompt] = useState('');
    const [genericLogline, setGenericLogline] = useState(false);
    const [genericSynopsis, setGenericSynopsis] = useState(false);
    const [outlineOnly, setOutlineOnly] = useState(false);

    const reset = useCallback(() => {
        setFile(null);
        setActPrompt('');
        setEpisodePrompt('');
        setScenePrompt('');
        setGenericLogline(false);
        setGenericSynopsis(false);
        setOutlineOnly(false);
    }, []);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [onClose, reset]);

    const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] ?? null;
        setFile(picked);
        e.target.value = '';
    }, []);

    const handleSubmit = () => {
        if (!file) return;
        onSubmit({ file, actPrompt, episodePrompt, scenePrompt, genericLogline, genericSynopsis, outlineOnly });
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: 'bg-c1 dark:bg-c37 rounded-xl' }}>
            <div className="flex flex-col gap-4 p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-medium leading-none text-c69 m-0">{t('import.advanced.title')}</h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-c45 hover:opacity-80 cursor-pointer bg-transparent border-none p-0 flex items-center"
                        aria-label={t('buttons.cancel')}
                    >
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-lg px-4 py-2 text-sm font-medium border border-c45 text-c18 dark:text-c4 bg-transparent hover:bg-c61 cursor-pointer"
                    >
                        {t('import.advanced.choose-file')}
                    </button>
                    <span className="text-sm text-c45 truncate">{file ? file.name : t('import.advanced.no-file')}</span>
                    <input ref={fileInputRef} type="file" accept=".docx" hidden onChange={handleFileChange} />
                </div>

                <div className="flex flex-col gap-1">
                    <label htmlFor="adv-act-prompt" className="text-xs leading-[18px] text-c45">
                        {t('import.advanced.act-prompt')}
                    </label>
                    <textarea
                        id="adv-act-prompt"
                        rows={2}
                        value={actPrompt}
                        onChange={e => setActPrompt(e.target.value)}
                        className={fieldClass}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label htmlFor="adv-episode-prompt" className="text-xs leading-[18px] text-c45">
                        {t('import.advanced.episode-prompt')}
                    </label>
                    <textarea
                        id="adv-episode-prompt"
                        rows={2}
                        value={episodePrompt}
                        onChange={e => setEpisodePrompt(e.target.value)}
                        className={fieldClass}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label htmlFor="adv-scene-prompt" className="text-xs leading-[18px] text-c45">
                        {t('import.advanced.scene-prompt')}
                    </label>
                    <textarea
                        id="adv-scene-prompt"
                        rows={2}
                        value={scenePrompt}
                        onChange={e => setScenePrompt(e.target.value)}
                        className={fieldClass}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-sm text-c18 dark:text-c4 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={genericLogline}
                            onChange={e => setGenericLogline(e.target.checked)}
                            className="accent-secondary h-4 w-4 cursor-pointer"
                        />
                        {t('import.advanced.generic-logline')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-c18 dark:text-c4 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={genericSynopsis}
                            onChange={e => setGenericSynopsis(e.target.checked)}
                            className="accent-secondary h-4 w-4 cursor-pointer"
                        />
                        {t('import.advanced.generic-synopsis')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-c18 dark:text-c4 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={outlineOnly}
                            onChange={e => setOutlineOnly(e.target.checked)}
                            className="accent-secondary h-4 w-4 cursor-pointer"
                        />
                        {t('import.advanced.outline-only')}
                    </label>
                </div>

                <div className="flex gap-2 mt-2">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!file || isPending}
                        className="flex-1 rounded-lg px-4 py-2 text-sm font-medium bg-secondary text-secondary-contrast hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer border-none"
                    >
                        {t('import.advanced.submit')}
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-[98px] rounded-lg px-4 py-2 text-sm font-medium border border-c45 text-c18 dark:text-c4 bg-transparent hover:bg-c61 cursor-pointer"
                    >
                        {t('buttons.cancel')}
                    </button>
                </div>
            </div>
        </Dialog>
    );
});

AdvancedImportDialog.displayName = 'AdvancedImportDialog';
